import { Fragment } from "react";

import type { Block, Inline, LegalDoc } from "../legal/content.generated";

// Rendert eine Folge von Inline-Runs (Text + Links) aus dem zentralen
// Rechtstext-Modell (shared/legal/content.ts → content.generated.ts).
function Inlines({ runs }: { runs: Inline[] }) {
  return (
    <>
      {runs.map((run, index) =>
        typeof run === "string" ? (
          <Fragment key={index}>{run}</Fragment>
        ) : (
          <a
            key={index}
            href={run.href}
            target={run.external ? "_blank" : undefined}
            rel={run.external ? "noopener noreferrer" : undefined}
            className="font-medium text-emerald-700 hover:text-emerald-800"
          >
            {run.label}
          </a>
        ),
      )}
    </>
  );
}

function BlockView({ block }: { block: Block }) {
  if (block.kind === "paragraph") {
    return (
      <p className="mt-3">
        <Inlines runs={block.content} />
      </p>
    );
  }
  if (block.kind === "address") {
    return (
      <address className="mt-3 not-italic">
        {block.lines.map((line, index) => (
          <div key={index}>
            <Inlines runs={line} />
          </div>
        ))}
      </address>
    );
  }
  return (
    <ul className="mt-3 list-disc space-y-1 pl-5">
      {block.items.map((item, index) => (
        <li key={index}>
          <Inlines runs={item} />
        </li>
      ))}
    </ul>
  );
}

export default function LegalDocument({ doc }: { doc: LegalDoc }) {
  return (
    <article>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
        {doc.title}
      </h1>
      {doc.updated && (
        <p className="mt-2 text-sm text-slate-500">Stand: {doc.updated}</p>
      )}
      <div className="mt-8 text-sm leading-relaxed text-slate-600">
        {doc.sections.map((section, index) => (
          <section key={index}>
            {section.heading && (
              <h2 className="mt-8 text-lg font-semibold text-slate-900">
                {section.heading}
              </h2>
            )}
            {section.blocks.map((block, blockIndex) => (
              <BlockView key={blockIndex} block={block} />
            ))}
          </section>
        ))}
      </div>
    </article>
  );
}
